import {ScrollView} from "react-native";
import {GroupInviteCandidates, GroupInviteRequest} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import ErrorPage from "@/lib/components/ErrorPage";
import {useQuery} from "@tanstack/react-query";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {Color, rowColor} from "@/lib/styles";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import AvatarRow from "@/lib/components/AvatarRow";
import {useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Message from "@/lib/components/Message";
import {useSafeAreaInsets} from "react-native-safe-area-context";
import {useGroup} from "@/lib/components/GroupProvider";
import AvatarTable from "@/lib/components/AvatarTable";

export default function InvitePage() {
    const {group_id} = useLocalSearchParams();
    const {session} = useSession();
    const inset = useSafeAreaInsets();
    const router = useRouter();

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const {group, groupLoading, groupError, refreshGroup} = useGroup();

    const inviteQuery = useQuery<GroupInviteCandidates>({
        queryKey: ['group', group_id, 'invite', session],
        queryFn: () => fetchBackend(`/group/${group_id}/invite`, "GET", session),
        retry: dataRetryPolicy,
    });

    if (groupError) {
        return <ErrorPage error={groupError}/>
    } else if (inviteQuery.error) {
        return <ErrorPage error={inviteQuery.error}/>
    } else if (groupLoading || inviteQuery.isPending || !group || !inviteQuery.data) {
        return <SpinnerPage/>
    }

    async function submitInvites() {
        setBusy(true);
        const payload = new GroupInviteRequest(selectedUsers);
        try {
            await fetchBackendNoContent(`/group/${group_id}/invite`, "POST", session, payload);
            await refreshGroup();
            router.dismiss();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    }

    function toggleUser(userID: number) {
        if (selectedUsers.includes(userID)) {
            setSelectedUsers(selectedUsers.filter(p => userID !== p));
        } else {
            setSelectedUsers([...selectedUsers, userID]);
        }
    }

    const candidate = inviteQuery.data;

    return (
        <Page>
            <Header title={`Inviterer til ${group.name}`}/>
            <ScrollView focusable={false}
                        indicatorStyle={"white"}
                        contentContainerStyle={{gap: 12}}
            >
                <Header title={"Bekjente"} icon={"relation"} marginTop={8}/>
                {candidate.users.length === 0 && <Message text={"Alle dine bekjente er allerede invitert til samfunnet."}/>}
                {candidate.users.length > 0 && (
                    <AvatarTable>
                        {candidate.users.map((avatar, index) => (
                            <AvatarRow key={avatar.id}
                                       avatar={avatar}
                                       badgeColor={avatar.color}
                                       color={rowColor(Color.Container, index)}
                                       contrastColor={avatar.contrast_color}
                                       onPress={() => toggleUser(avatar.id)}
                                       checked={selectedUsers.includes(avatar.id)}
                            />
                        ))}
                    </AvatarTable>
                )}
            </ScrollView>

            {error && <ErrorMessage error={error}/>}

            <Nav style={{marginBottom: 8 + inset.bottom}}>
                <ActionButton onPress={submitInvites}
                              title={`Send invitasjoner ( ${selectedUsers.length} )`}
                              busy={busy}
                />
            </Nav>
        </Page>
    )
}